import {
  NodeId,
  ROOT_NODE,
  SerializedNode,
  SerializedNodes,
} from "@craftjs/core";

import logger from "@/utils/logger";

export default function getResolveableNodes(
  parsedNodes: Record<string, SerializedNode>,
  blocks: { [k: string]: React.ComponentType<any> }
): SerializedNodes {
  const resolveableNodes = JSON.parse(JSON.stringify(parsedNodes));

  function removeFromParent(currentNodeId: NodeId, parentNodeId: NodeId) {
    const parentNode = parsedNodes[parentNodeId];

    // For `nodes`, it's simple and we just delete the reference to this `currentNodeId`
    if (parentNode.nodes.indexOf(currentNodeId) !== -1) {
      resolveableNodes[parentNodeId] = {
        ...resolveableNodes[parentNodeId],
        nodes: resolveableNodes[parentNodeId].nodes.filter(
          (id: NodeId) => id !== currentNodeId
        ),
      };
    } else {
      // If not in `nodes` then this must be a `linkedNode` for the parent.
      // It's a bit trickier here - we need to delete the parent completely
      // because a linkedNode signifies that the parent's component actually
      // depends on the linkedNode in order to render.
      delete resolveableNodes[parentNodeId];
      removeFromParent(parentNodeId, parentNode.parent);
    }
  }

  const loggerKeys = {};
  function getDescendants(nodeId: NodeId) {
    const node = parsedNodes[nodeId];

    if (!node) {
      return;
    }

    // `linkedNodes` are the `Element`s that _always_ render inside of the
    // current node; think `BrandMaxiCover` has 4 linkedNodes – 3 Texts, and 1 Action
    const linkedNodes = node.linkedNodes;
    Object.keys(linkedNodes).forEach((key) => {
      const nodeId = linkedNodes[key];
      getDescendants(nodeId);
    });

    // `nodes` are the direct children of `Element`s that aren't part of the
    // Element's design, for instance there are 3 `Text` nodes here that are
    // rendered within a `SomeList`:
    // <Element is={SomeList} id="some-list">
    //   <Text />
    //   <Text />
    //   <Text />
    // </Element>
    const childNodes = node.nodes;
    childNodes.forEach((nodeId) => getDescendants(nodeId));

    const componentName =
      typeof node.type === "string" ? node.type : node.type.resolvedName;

    // This component doesn't seem to exist in the blocks hash, oh no!
    // To ensure the page doesn't crash, we'll remove this specific node reference.
    if (!blocks[componentName]) {
      // Dedupe the logs per componentName
      const error = `${
        componentName || node.displayName
      } is missing from the resolver, removing from tree`;

      if (!loggerKeys[error]) {
        loggerKeys[error] = true;
        logger.error(new Error(error), {
          details: "missing resolver",
        });
      }

      if (node.parent) {
        delete resolveableNodes[nodeId];
        removeFromParent(nodeId, node.parent);
      }
    }
  }

  getDescendants(ROOT_NODE);
  return resolveableNodes;
}
